import { authService } from '../services/auth.service';
import { securitySettingsService } from '../services/securitySettings.service';

/**
 * Check if token is expired
 * @param token - JWT token
 * @returns boolean indicating if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true; // If we can't parse the token, consider it expired
  }
};

/**
 * Validate current token and logout if expired
 * @returns boolean indicating if user is authenticated
 */
export const validateToken = (): boolean => {
  const token = authService.getToken();
  
  if (!token) {
    return false;
  }
  
  if (isTokenExpired(token)) {
    // Token is expired, logout user
    authService.clearAuth();
    window.location.href = '/login';
    return false;
  }
  
  return true;
};

/**
 * Setup periodic token validation using security settings
 */
export const setupTokenValidation = async (): Promise<void> => {
  try {
    // Get security settings from backend
    const settings = await securitySettingsService.getSecuritySettings();
    const interval = settings.token_check_interval * 60 * 1000; // Convert minutes to milliseconds
    
    // Check token immediately
    validateToken();
    
    // Set up periodic checks
    setInterval(() => {
      validateToken();
    }, interval);
  } catch (error) {
    // Fallback to default 5 minutes if settings can't be loaded

    validateToken();
    setInterval(() => {
      validateToken();
    }, 5 * 60 * 1000);
  }
};

/**
 * Handle API errors and auto logout
 * @param error - API error response
 */
export const handleApiError = (error: any): void => {
  if (error.response?.status === 401 || error.response?.status === 403) {
    // Token is invalid, logout user
    authService.clearAuth();
    window.location.href = '/login';
    
    // Show notification if available
    if (window.showNotification) {
      window.showNotification('Session expired. Please login again.', 'warning');
    }
  }
}; 